import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { pendingNavigateCallbacks } from '../constants/pendingNavigateCallbacks';
import { NavigateWithCallback } from '../types/NavigateWithCallback';

/**
 * Custom hook that wraps react-router's navigate to support post-navigation callbacks.
 * A callback runs once, after the next location change has been committed, wherever
 * that navigation ends up (ex: a loader redirect). The page that navigated has been
 * unmounted by then, so resetting its store does not flash an emptied page.
 * Callbacks are stored globally to survive component unmounting during navigation.
 *
 * @example
 * const navigateWithCallback = useNavigateWithCallback();
 *
 * navigateWithCallback('/next-page', (): void => {
 *   // This runs after navigation completes
 *   resetStore(useApplyStore);
 * });
 */
export function useNavigateWithCallback(): NavigateWithCallback {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect((): void => {
    // every committed navigation has a new key, so anything registered from
    // another location has been navigated away from
    pendingNavigateCallbacks.forEach((pending): void => {
      if (pending.fromKey === location.key) {
        return;
      }

      pendingNavigateCallbacks.delete(pending);
      pending.callback();
    });
  }, [location.key]);

  return (to, callback, options): void => {
    pendingNavigateCallbacks.add({ callback, fromKey: location.key });

    navigate(to, options);
  };
}
